import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { FaChevronDown } from 'react-icons/fa';
import { ExtendedMarkerData, MarkerAction } from '../../../types/marker';
import { ActionType } from '../../../types/action';

import { ContactSection } from './components/ContactSection';
import ImageUpload from './components/ImageUpload/ImageUpload';
import ActionInput from './components/ActionSection/ActionInput';
import ActionList from './components/ActionList';
import { CollapsibleHeader, CollapsibleTitle, CollapseIcon, CollapsibleContent, MissingText } from './components/CollapsibleSection';
import {
  ModalContainer,
  ModalHeader,
  ContentContainer,
  FormSection,
  SectionTitle,
  ButtonContainer,
  AddressDisplay,


} from './styles';
import Button from '../../common/Button';

const MissingPhotoText = styled(MissingText)`
  margin-bottom: -8px;
`;

import { Select } from '../../common/Select';
import { useAppContext } from '../../../context/AppContext';
import Modal from '../Modal';

import logger from '../../../utils/logger';
import { actionsService } from '../../../services/supabase/actions.service';
import { useAuth } from '../../../context/AuthContext';
import { useUserFilter } from '../../../context/UserFilterContext';

// Import des hooks personnalisés extraits
import { 
  useMarkerForm, 
  useMarkerActions, 
  useMarkerNotifications, 
  useMarkerSubmit, 
  useCategoryColors 
} from './hooks';

interface UnifiedMarkerModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  marker?: ExtendedMarkerData;
  onClose: () => void;
  onSave: (data: ExtendedMarkerData) => Promise<ExtendedMarkerData>;
  markerAddress?: string;  // Adresse du marqueur sur la carte
  _notificationId?: string;  // ID de la notification associée (non utilisé, mais conservé pour compatibilité)
  _notificationTitle?: string;  // Titre de la notification associée (non utilisé, mais conservé pour compatibilité)
  _actionId?: string;  // ID de l'action à mettre en évidence (via notification)
}

export const UnifiedMarkerModal: React.FC<UnifiedMarkerModalProps> = (props) => {
  const {
    isOpen,
    mode,
    marker,
    onClose,
    onSave,
    markerAddress,
    _notificationId,
    _notificationTitle,
    _actionId,
  } = props;
  
  // Context global de l'application
  const { state, dispatch } = useAppContext();
  
  // Récupérer les informations utilisateur pour vérifier les permissions
  const { userProfile } = useAuth();
  const { selectedUserId } = useUserFilter();
  
  // Déterminer si l'admin consulte un marker d'un autre utilisateur
  const isAdminViewingOtherUserMarker = useMemo(() => {
    const isAdmin = userProfile?.is_owner || false;
    const isMultiUserMode = selectedUserId === null; // Mode "Tous les utilisateurs"
    const markerBelongsToOtherUser = marker?.user_id && marker.user_id !== userProfile?.id;
    
    return isAdmin && isMultiUserMode && markerBelongsToOtherUser;
  }, [userProfile, selectedUserId, marker]);
  
  // Utilisation du hook useMarkerForm pour gérer l'état du formulaire
  const { 
    formData, 
    setFormData, 
    selectKey, 
    originalCategoryId
  } = useMarkerForm({ 
    marker, 
    mode, 
    markerAddress, 
    isOpen 
  });
  
  // Utilisation du hook useMarkerActions pour gérer les actions du marqueur
  const {
    // États
    newActionType,
    setNewActionType,
    newActionDetails,
    setNewActionDetails,
    newCustomType,
    setNewCustomType,
    showNewTypeInput,
    setShowNewTypeInput,
    reminderDate,
    setReminderDate,
    tempReminderDate,
    setTempReminderDate,
    showDatePicker,
    setShowDatePicker,
    
    // États d'édition
    editingAction,
    setEditingAction,
    editActionType,
    setEditActionType,
    editActionDetails,
    setEditActionDetails,
    editReminderDate,
    setEditReminderDate,
    editTempReminderDate: _editTempReminderDate,
    setEditTempReminderDate: _setEditTempReminderDate,
    showEditDatePicker: _showEditDatePicker,
    setShowEditDatePicker: _setShowEditDatePicker,
    
    // Fonctions
    createCustomAction,
    mergeActions,
    createCategoryChangeAction,
    addNewAction,
    updateAction,
    deleteAction: _deleteAction
  } = useMarkerActions({ 
    markerId: marker?.id || '', 
    isOpen 
  });
  
  // Utilisation du hook useMarkerNotifications pour gérer les notifications
  const { readAlerts: _readAlerts, toggleAlertRead: _toggleAlertRead, canToggleNotification: _canToggleNotification } = useMarkerNotifications({ isOpen });
  
  // État local pour gérer l'expansion du texte des actions
  const [isPhotoExpanded, setIsPhotoExpanded] = useState(false);
  
  // Forcer l'ouverture si une photo est présente
  useEffect(() => {
    if (formData.imageUrl) {
      setIsPhotoExpanded(true);
    }
  }, [formData.imageUrl]);
  
  // État pour l'action mise en évidence (via notification)
  const [_highlightedActionId, _setHighlightedActionId] = useState<string | null>(null);
  const [_expandedActions, _setExpandedActions] = useState<Set<string>>(new Set());
  
  // Fonction pour toggle l'expansion d'une action
  const _toggleActionExpansion = (actionId: string) => {
    _setExpandedActions((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };
  
  // Utilisation du hook personnalisé pour les couleurs de catégorie
  const { getCategoryBackgroundColor, getCategoryBorderColor } = useCategoryColors(formData?.categoryId || null);
  
  // Utilisation du hook useMarkerSubmit pour gérer la soumission du formulaire
  const {
    errors,
    handleSubmit
  } = useMarkerSubmit({
    mode,
    marker,
    formData,
    originalCategoryId,
    onSave,
    onClose,
    createCustomAction,
    mergeActions,
    createCategoryChangeAction
  });
  
  useEffect(() => {
    if (marker) {
      logger.info('🔄 Mise à jour du marker dans la modale', {
        current: {
          id: formData.id,
          categoryId: formData.categoryId,
          actionsCount: formData.actions?.length,
          lastAction: formData.actions?.[formData.actions?.length - 1]
        },
        new: {
          id: marker.id,
          categoryId: marker.categoryId,
          actionsCount: marker.actions?.length,
          lastAction: marker.actions?.[marker.actions?.length - 1]
        }
      });

      // Si la modale est ouverte via une notification, mettre en évidence l'action correspondante
      if (_actionId && marker.actions) {
        // Chercher l'action correspondante via son ID
        const targetAction = marker.actions.find(action => action.id === _actionId);
        
        if (targetAction) {
          _setHighlightedActionId(targetAction.id);
          
          // Supprimer la mise en évidence après 5 secondes
          setTimeout(() => {
            _setHighlightedActionId(null);
          }, 5000);
        }
      }

      // Charger explicitement les actions du marqueur depuis la base de données
      const loadMarkerWithActions = async () => {
        try {
          // Récupérer le marqueur avec toutes ses actions depuis la base de données
          if (marker?.id) {
            logger.info('🔄 Rechargement des actions depuis Supabase pour le marqueur:', marker.id);
            
            // Charger les actions depuis Supabase
            const actions = await actionsService.getByMarkerId(marker.id);
            
            console.log('🔍 [DEBUG] Actions récupérées depuis Supabase lors rechargement:', {
              markerId: marker.id,
              actionsCount: actions.length,
              actions: actions.map(a => ({
                id: a.id,
                type: a.type,
                description: a.description,
                is_system: a.is_system,
                markerId: a.markerId,
                date: a.date
              })),
              timestamp: new Date().toISOString()
            });
            
            logger.info('📋 Actions récupérées depuis Supabase:', {
              markerId: marker.id,
              actionsCount: actions.length,
              actions: actions.map(a => ({
                id: a.id,
                type: a.type,
                description: a.description,
                is_system: a.is_system
              }))
            });
            
            // Créer le marqueur mis à jour avec les actions fraîches
            const updatedMarker = {
              ...marker,
              actions: actions
            };
            
            return updatedMarker;
          }
          
          // Fallback au comportement précédent si pas d'ID
          const updatedMarker = marker ? { ...marker } : null;
          
          if (updatedMarker) {
            logger.info('📋 Marqueur récupéré depuis la base de données:', {
              id: updatedMarker.id,
              actionsCount: updatedMarker.actions?.length,
              actions: updatedMarker.actions?.map(a => ({
                id: a.id,
                type: a.type,
                description: a.description,
                is_system: a.is_system
              }))
            });
            
            // Mettre à jour le formulaire avec les données récupérées
            const updatedFormData = {
              ...updatedMarker,
              // Extraire le prénom du premier contact s'il existe
              firstName: updatedMarker.contacts && updatedMarker.contacts.length > 0 
                ? updatedMarker.contacts[0].firstName || '' 
                : '',
              actions: updatedMarker.actions?.map((a: MarkerAction) => ({
                ...a,
                date: a.date instanceof Date ? a.date : new Date(a.date)
              })) || []
            };
            
            setFormData(updatedFormData);
          } else {
            // Fallback au comportement précédent si la récupération échoue
            const updatedFormData = marker ? {
              ...marker,
              firstName: marker.contacts && marker.contacts.length > 0 
                ? marker.contacts[0].firstName || marker.contacts[0].name || '' 
                : '',
              actions: marker.actions?.map((a: MarkerAction) => ({
                ...a,
                date: a.date instanceof Date ? a.date : new Date(a.date)
              })) || []
            } : { id: '', coordinates: [0, 0] as [number, number], address: '', categoryId: null, actions: [] as MarkerAction[] };
            
            setFormData(updatedFormData);
          }
        } catch (error) {
          logger.error('Erreur lors de la récupération du marqueur avec ses actions:', error);
          
          // Fallback au comportement précédent en cas d'erreur
          const updatedFormData = {
            ...marker,
            firstName: marker.contacts && marker.contacts.length > 0 
              ? marker.contacts[0].firstName || marker.contacts[0].name || '' 
              : '',
            actions: marker.actions?.map((a: any) => ({
              ...a,
              date: a.date instanceof Date ? a.date : new Date(a.date)
            })) || []
          };
          
          setFormData(updatedFormData);
        }
      };
      
      loadMarkerWithActions();
    }
  }, [marker]);

  useEffect(() => {
    logger.info('📊 État actuel du formData', {
      id: formData.id,
      categoryId: formData.categoryId,
      actionsCount: formData.actions?.length,
      actions: formData.actions
    });
  }, [formData]);

  useEffect(() => {
    if (isOpen && formData.id) {
      // Récupérer les actions temporaires depuis Supabase
      const loadTemporaryActions = async () => {
        try {
          // Temporairement désactivé en attendant l'injection correcte du service
          const tempActions: MarkerAction[] = [];
          // Simuler un appel de service pour éviter les erreurs de compilation
          console.log(`Tentative de chargement des actions temporaires pour le marqueur ${formData.id}`);
          
          if (tempActions.length > 0) {
            logger.info(`Chargement de ${tempActions.length} actions temporaires depuis Supabase pour le marqueur ${formData.id}`);
            
            // Fusionner les actions existantes avec les actions temporaires
            // en évitant les doublons basés sur l'ID
            const existingActionIds = new Set((formData.actions || []).map((a: MarkerAction) => a.id));
            const newTempActions = tempActions.filter((a: MarkerAction) => !existingActionIds.has(a.id));
            
            if (newTempActions.length > 0) {
              setFormData(prev => ({
                ...prev,
                actions: [...(prev.actions || []), ...newTempActions]
              }));
              logger.info(`${newTempActions.length} actions temporaires ajoutées au formData`);
            }
          }
        } catch (error) {
          logger.error('Erreur lors du chargement des actions temporaires:', error);
        }
      };
      
      loadTemporaryActions();
    }
  }, [isOpen, formData.id]);

  // Fonction handleClose pour fermer la modale
  const handleClose = () => {
    onClose();
  };

  // Fonction pour ajouter une action personnalisée
  const handleAddAction = () => {
    if (!newActionType || !newActionDetails) {
      return;
    }

    // Sauvegarder le type d'action actuel pour le conserver après l'ajout
    const currentActionType = newActionType;
    
    // Créer une nouvelle action
    addNewAction(newActionType as ActionType, newActionDetails, reminderDate || null)
      .then((newAction: MarkerAction | null) => {
        if (newAction) {
          // Mettre à jour le formData avec la nouvelle action
          setFormData(prev => ({
            ...prev,
            actions: [...(prev.actions || []), newAction as MarkerAction]
          }));
          
          // Réinitialiser les champs mais conserver le type d'action
          // setNewActionType(''); // Ne pas réinitialiser le type d'action pour le conserver
          setNewActionDetails(''); // Réinitialiser seulement les détails
          setReminderDate(null);
          setTempReminderDate(null);
          setShowDatePicker(false);
          setShowNewTypeInput(false);
          
          // Force le rendu du Select avec la valeur conservée
          setTimeout(() => {
            logger.debug('Force le rendu du Select avec la valeur conservée:', currentActionType);
            // Utiliser une fonction pour garantir que nous avons la dernière valeur
            setNewActionType(prev => prev); // Cela force un re-rendu sans changer la valeur
          }, 0);
          
          logger.debug('Type d\'action conservé après ajout:', currentActionType);
        }
      });
  };

  // Fonction pour ajouter un type personnalisé (non utilisée actuellement)
  const _handleAddCustomType = () => {
    if (newCustomType.trim()) {
      const newType = newCustomType.trim();
      
      // Ajouter le nouveau type à la liste globale des types d'actions personnalisés
      dispatch({ type: 'ADD_CUSTOM_ACTION_TYPE', payload: newType });
      
      // Définir ce nouveau type comme sélectionné
      setNewActionType(newType);
      setShowNewTypeInput(false);
      setNewCustomType(''); // Réinitialiser le champ de saisie
      
      logger.debug('Nouveau type d\'action ajouté:', newType);
      logger.debug('Liste des types d\'actions après ajout:', [...state.customActionTypes, newType]);
    }
  };

  // Fonction pour gérer la sauvegarde d'une action en cours d'édition
  const _handleSaveEdit = async () => {
    if (!editingAction || !editActionType || !editActionDetails) return;
    
    const updatedAction: MarkerAction = {
      ...editingAction,
      type: editActionType as ActionType,
      description: editActionDetails,
      reminderDate: editReminderDate ?? undefined
    };
    
    try {
      // Appeler updateAction pour sauvegarder en base
      await updateAction(updatedAction);
      
      // Mettre à jour l'action dans le formData
      setFormData(prev => ({
        ...prev,
        actions: (prev.actions || []).map(a => 
          a.id === editingAction.id ? updatedAction : a
        )
      }));
      
      // Réinitialiser l'état d'édition
      handleCancelEdit();
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'action:', error);
    }
  };

  // Fonction pour annuler l'édition d'une action
  const handleCancelEdit = () => {
    setEditingAction(null);
    setEditActionType('');
    setEditActionDetails('');
    setEditReminderDate(null);
    _setEditTempReminderDate(null);
    _setShowEditDatePicker(false);
  };


  // Forcer le rendu du select avec une nouvelle clé
  const setSelectKey = (key: string) => {
    // Cette fonction est maintenant définie localement pour éviter les erreurs
    // Elle était précédemment extraite dans useMarkerForm mais n'est pas exportée
    logger.debug('setSelectKey appelé avec la clé:', key);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} backgroundColor={getCategoryBackgroundColor}>
      <ModalContainer style={{ borderColor: getCategoryBorderColor }}>
        <ModalHeader>
          <h2>{mode === 'create' ? 'Nouveau Marqueur' : 'Modifier le Marqueur'}</h2>
          <Button 
            $variant="text" 
            onClick={handleClose} 
            className="enhanced-contrast-button"
          >
            Fermer
          </Button>
        </ModalHeader>

        <ContentContainer>
          <form onSubmit={handleSubmit}>
            <AddressDisplay>
              {mode === 'edit' ? formData.address : markerAddress || "Position du marqueur non définie"}
            </AddressDisplay>

            <FormSection>
              <Select
                key={selectKey} // Utiliser une clé unique pour forcer le rendu
                label="Catégorie"
                value={formData.categoryId || ''}
                onChange={(e) => {
                  const newCategoryId = e.target.value || null;
                  
                  console.log('🔄 SELECT - Changement de catégorie', {
                    oldCategoryId: formData.categoryId,
                    newCategoryId,
                    selectKey,
                    timestamp: new Date().toISOString()
                  });
                  
                  // Forcer le rendu du select avec une nouvelle clé
                  setSelectKey(newCategoryId || `change-${Date.now()}`);
                  
                  // Mettre à jour uniquement la catégorie dans formData
                  // L'action de changement de catégorie sera créée automatiquement lors de la soumission
                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      categoryId: newCategoryId
                    };
                    
                    console.log('📝 FORMDATA - Après mise à jour catégorie', {
                      oldCategoryId: prev.categoryId,
                      newCategoryId: updated.categoryId,
                      timestamp: new Date().toISOString()
                    });
                    
                    return updated;
                  });
                }}
                options={[
                  { value: '', label: 'Sélectionner...' },
                  ...state.categories.map(category => ({
                    value: category.id,
                    label: category.name
                  }))
                ]}
                error={errors.categoryId}
                disabled={isAdminViewingOtherUserMarker || false}
              />
            </FormSection>

            <FormSection>
              <CollapsibleHeader onClick={() => setIsPhotoExpanded(!isPhotoExpanded)}>
                <CollapsibleTitle>Photo du bien</CollapsibleTitle>
                <CollapseIcon $isExpanded={isPhotoExpanded}>
                  <FaChevronDown />
                </CollapseIcon>
              </CollapsibleHeader>
              
              {!isPhotoExpanded && !formData.imageUrl && (
                <MissingPhotoText>Photo manquante</MissingPhotoText>
              )}
              
              <CollapsibleContent $isExpanded={isPhotoExpanded}>
                <ImageUpload
                imageUrl={formData.imageUrl}
                onImageChange={(imageUrl) => {
                  console.log('🖼️ DEBUG onImageChange appelé:', {
                    newImageUrl: imageUrl ? `${imageUrl.substring(0, 50)}...` : 'null',
                    currentImageUrl: formData.imageUrl ? `${formData.imageUrl.substring(0, 50)}...` : 'undefined',
                    timestamp: new Date().toISOString()
                  });
                  setFormData(prev => ({
                    ...prev,
                    imageUrl: imageUrl || undefined
                  }));
                }}
                disabled={isAdminViewingOtherUserMarker || false}
              />
              </CollapsibleContent>
            </FormSection>

            <ContactSection 
              formData={formData}
              setFormData={setFormData}
              errors={errors as Record<string, string>}
              isReadOnly={isAdminViewingOtherUserMarker || false}
            />

            <FormSection>
              <SectionTitle>Action</SectionTitle>
              <ActionInput
                // États pour les actions
                newActionType={newActionType}
                setNewActionType={setNewActionType}
                newActionDetails={newActionDetails}
                setNewActionDetails={setNewActionDetails}
                newCustomType={newCustomType}
                setNewCustomType={setNewCustomType}
                showNewTypeInput={showNewTypeInput}
                setShowNewTypeInput={setShowNewTypeInput}
                
                // États pour le DatePicker
                reminderDate={reminderDate}
                setReminderDate={setReminderDate}
                tempReminderDate={tempReminderDate}
                setTempReminderDate={setTempReminderDate}
                showDatePicker={showDatePicker}
                setShowDatePicker={setShowDatePicker}
                
                // Fonction de gestion
                onAddAction={handleAddAction}
                
                // Données du contexte
               customActionTypes={state.customActionTypes}
               isReadOnly={isAdminViewingOtherUserMarker || false}
              />
            </FormSection>

            <FormSection>
              <ActionList
                  actions={formData.actions || []}
                  expandedActions={_expandedActions}
                  toggleActionExpansion={_toggleActionExpansion}
                  canToggleNotification={_canToggleNotification}
                  isReadOnly={isAdminViewingOtherUserMarker || false}
                />
            </FormSection>

            <ButtonContainer>
              {isAdminViewingOtherUserMarker ? (
                <Button type="button" onClick={handleClose} $variant="primary">
                  Fermer
                </Button>
              ) : (
                <>
                  <Button type="button" onClick={handleClose}>
                    Annuler
                  </Button>
                  <Button type="submit" $variant="primary">
                    {mode === 'create' ? 'Créer' : 'Mettre à jour'}
                  </Button>
                </>
              )}
            </ButtonContainer>
          </form>
        </ContentContainer>
      </ModalContainer>
      {/* Ancien système GlobalTooltip supprimé - remplacé par le système unifié dans chaque composant */}
    </Modal>
  );
};

export default UnifiedMarkerModal;

